import { Prescription, PrescriptionResponse, Refraction } from './refraction'
import z from 'zod'

type ActionConfig = (ref: Refraction) => Partial<Refraction> | undefined

export const actions = {
  sum_sph_add: (ref) => {
    return {
      r_sph: ref.r_sph + ref.r_add,
      l_sph: ref.l_sph + ref.l_add,
      l_add: 0,
      r_add: 0,
    }
  },
  reset_add: () => {
    return {
      l_add: 0,
      r_add: 0,
    }
  },
} as const satisfies Record<string, ActionConfig>

export type RefractionAction = keyof typeof actions

const actionKeys = Object.fromEntries(
  Object.keys(actions).map((k) => [k, k])
) as Record<RefractionAction, RefractionAction>

export const refractionActionSchema = z.nativeEnum(actionKeys)

const isPrescriptionResponse = (
  ref: Refraction | Prescription | PrescriptionResponse
): ref is PrescriptionResponse => 'id' in ref

// Prescription record fields, not refraction: an order carries `optometrist`, the
// name is only for display. Fields we do not own are left alone, see refractionSchema
export const sanitizeRefraction = (
  ref: Refraction | Prescription | PrescriptionResponse
): Refraction => {
  if (isPrescriptionResponse(ref)) {
    const { id, remark, name, create_date, optometrist_name, ...refraction } =
      ref
    return refraction satisfies Refraction
  }
  return ref
}

export const applyRefractionAction = (
  ref: Refraction,
  action?: RefractionAction
) => {
  if (!action) {
    return ref
  }
  const callback = actions[action]
  if (!callback) {
    return ref
  }
  const result = callback(ref)
  return {
    ...ref,
    ...result,
  }
}

export const applyRefractionActions = (
  input: Refraction | Prescription | PrescriptionResponse | undefined | null,
  state: { action?: RefractionAction }[],
  pd?: { l_pd: number; r_pd: number } | null
) => {
  if (!input) {
    return input
  }
  const ref = sanitizeRefraction(input)
  const withPd = pd ? { ...ref, ...pd } : ref
  const actions = state.filter((s) => s.action)

  if (!actions.length) {
    return withPd
  }
  return actions.reduce(
    (acc, { action }) => applyRefractionAction(acc, action),
    withPd
  )
}
